import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import type { RootState } from "@/store/store";
import { parseURLParams } from "@/utils/url";
import {
  BRANDS,
  FILTERS,
  MARKETS,
} from "@/constant/constantsThrougoutProject";
import type {
  CurrentScreenContext,
  FilterRef,
  BrandRef,
  MarketRef,
} from "../types/screenContext";
import { DEFAULT_SCREEN_CONTEXT } from "../types/screenContext";

/** Maps `/:screen/:subRoute` (segment | brand | market) to assistant `groupedBy`. */
const SUB_ROUTE_TO_GROUPED_BY: Record<string, string> = {
  segment: "SEGMENT",
  brand: "BRAND",
  market: "MARKET",
};

/**
 * Builds the v2 `currentScreenContext` payload from Redux store state.
 *
 * Returns a stable `buildCurrentScreenContext()` function that produces the
 * payload expected by `/ai-assistant/usermessage`.
 *
 * On standard analytic routes (`/:screen/:subRoute`), `groupedBy` reflects the
 * active By * tab (SEGMENT / BRAND / MARKET) from the path, not only the Group
 * By dropdown in Redux.
 */
export function useScreenContext() {
  const location = useLocation();

  const userSelection = useSelector(
    (state: RootState) => state.userSelection,
  );
  const brandEntities = useSelector(
    (state: RootState) => state.activeGroup.brands.entities,
  );
  const marketEntities = useSelector(
    (state: RootState) => state.activeGroup.markets.markets.entities,
  );
  const filterEntities = useSelector(
    (state: RootState) => state.activeGroup.filters.filters.entities,
  );

  const buildCurrentScreenContext = useCallback((): CurrentScreenContext => {
    const pathSegments = (location?.pathname || "")
      .split("/")
      .filter(Boolean);
    const module = pathSegments[0] || "";
    const subRouteKey = pathSegments[1]?.toLowerCase();
    const groupedByFromRoute = subRouteKey
      ? SUB_ROUTE_TO_GROUPED_BY[subRouteKey]
      : undefined;

    const rawParams = parseURLParams(
      new URLSearchParams(location?.search || ""),
    );

    // --- brands: [{id, name}] --- (support both singular and plural state)
    let brandIds = [...(userSelection.brands ?? [])];
    if (userSelection.brands?.length > 0 && !brandIds.includes(userSelection.brands[0])) {
      brandIds.push(userSelection.brands[0]);
    }
    if (brandIds.length === 0) {
      const fromUrl = rawParams[BRANDS];
      if (Array.isArray(fromUrl)) {
        brandIds = fromUrl as number[];
      }
    }
    const brands: BrandRef[] = brandIds
      .map((id: number) => {
        const entity = brandEntities[id];
        return entity
          ? { id: entity.id, name: entity.displayName || entity.name }
          : null;
      })
      .filter(Boolean) as BrandRef[];

    // --- markets: [{id, name}] --- (support both singular and plural state)
    let marketIds = [...(userSelection.markets ?? [])];
    if (
      userSelection.markets?.length > 0 &&
      !marketIds.includes(userSelection.markets[0])
    ) {
      marketIds.push(userSelection.markets[0]);
    }
    if (marketIds.length === 0) {
      const fromUrl = rawParams[MARKETS];
      if (Array.isArray(fromUrl)) {
        marketIds = fromUrl as number[];
      }
    }
    const markets: MarketRef[] = marketIds
      .map((id: number) => {
        const entity = marketEntities[id];
        return entity ? { id: entity.id, name: entity.name } : null;
      })
      .filter(Boolean) as MarketRef[];

    // --- metricKeys ---
    const metricKeys: string[] = [];
    if (userSelection.trendsMetric) {
      metricKeys.push(userSelection.trendsMetric);
    }

    // --- filters: [{type, filterId, filterOptions}] ---
    const selectedIdsSource =
      userSelection.filters && userSelection.filters.length > 0
        ? userSelection.filters
        : (rawParams[FILTERS] as number[] | undefined) ?? [];
    const selectedOptionIds = new Set(selectedIdsSource ?? []);
    const filters: FilterRef[] = [];

    if (selectedOptionIds.size > 0 && filterEntities) {
      const filterIds = Object.keys(filterEntities);
      for (const fid of filterIds) {
        const filter = filterEntities[Number(fid)];
        if (!filter?.filterOptions) continue;

        const matchingOptions = filter.filterOptions
          .filter((opt) => selectedOptionIds.has(opt.filterOptionId))
          .map((opt) => opt.filterOptionId);

        if (matchingOptions.length > 0) {
          filters.push({
            type: filter.filterType || "DEFAULT",
            filterId: filter.id,
            filterOptions: matchingOptions,
          });
        }
      }
    }

    // --- period: {type, startDate?, endDate?} ---
    const periodType = userSelection.periodType || "";
    const period =
      periodType === "CUSTOM"
        ? {
            type: periodType,
            startDate: userSelection.periodStartDate || undefined,
            endDate: userSelection.periodEndDate || undefined,
          }
        : { type: periodType };

    return {
      brands,
      markets,
      module,
      metricKeys,
      // Some screens store segment selection under `userSelection.segments`
      // (e.g. segment selector routes). Prefer that when present.
      segments:
        Array.isArray(userSelection.segments) &&
        userSelection.segments.length > 0
          ? userSelection.segments
          : userSelection.segments ?? [],
      filters,
      audienceBase: userSelection.base || DEFAULT_SCREEN_CONTEXT.audienceBase,
      viewMode: userSelection.mode || DEFAULT_SCREEN_CONTEXT.viewMode,
      visualizationType:
        userSelection.visualizationType ||
        DEFAULT_SCREEN_CONTEXT.visualizationType,
      groupedBy:
        groupedByFromRoute ??
        (userSelection.groupedBy || DEFAULT_SCREEN_CONTEXT.groupedBy),
      statTest: userSelection.statTest || DEFAULT_SCREEN_CONTEXT.statTest,
      intervalType: userSelection.period || DEFAULT_SCREEN_CONTEXT.intervalType,
      period,
    };
  }, [
    location?.pathname,
    userSelection,
    brandEntities,
    marketEntities,
    filterEntities,
  ]);

  return { buildCurrentScreenContext };
}
